import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";

function randInt(min, max, exclude = []) {
  let n;
  do {
    n = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (exclude.includes(n));
  return n;
}

function choice(items) {
  return items[Math.floor(Math.random() * items.length)];
}

function quadraticValue(a, b, c, x) {
  return a * x * x + b * x + c;
}

function weightedX0() {
  const outer = [-4, -3, 3, 4];
  const inner = [-2, -1, 0, 1, 2];
  return Math.random() < 0.75 ? choice(outer) : choice(inner);
}

function buildWindow(center, span = 10) {
  let start = Math.round(center) - span / 2;
  let end = start + span;

  if (start > 0) {
    end -= start;
    start = 0;
  } else if (end < 0) {
    start -= end;
    end = 0;
  }

  return [Math.round(start), Math.round(end)];
}

function buildRanges(x0, y0) {
  return {
    xRange: buildWindow(x0),
    yRange: buildWindow(y0),
  };
}

function simplifyFraction(numerator, denominator) {
  const gcd = (a, b) => (b === 0 ? Math.abs(a) : gcd(b, a % b));
  const sign = Math.sign(numerator) * Math.sign(denominator);
  const absNum = Math.abs(numerator);
  const absDen = Math.abs(denominator);
  const d = gcd(absNum, absDen);
  const simpNum = absNum / d;
  const simpDen = absDen / d;
  if (simpDen === 1) return `${sign * simpNum}`;
  return `${sign < 0 ? "-" : ""}\\frac{${simpNum}}{${simpDen}}`;
}

function quadraticLatex(a, b, c) {
  const parts = [];
  const aPart = a === 1 ? "x^2" : a === -1 ? "-x^2" : `${a}x^2`;
  parts.push(aPart);
  if (b !== 0) {
    const bPart = Math.abs(b) === 1 ? "x" : `${Math.abs(b)}x`;
    parts.push(b > 0 ? `+ ${bPart}` : `- ${bPart}`);
  }
  if (c !== 0) {
    parts.push(c > 0 ? `+ ${c}` : `- ${Math.abs(c)}`);
  }
  return parts.join(" ");
}

function lineLatex(m, d) {
  if (m === 0) return `${d}`;
  const mPart = m === 1 ? "x" : m === -1 ? "-x" : `${m}x`;
  if (d === 0) return mPart;
  return d > 0 ? `${mPart} + ${d}` : `${mPart} - ${Math.abs(d)}`;
}

function slopeLatex(numerator, denominator) {
  return simplifyFraction(numerator, denominator);
}

function lineLatexExact(mNumerator, mDenominator, d) {
  const mValue = mNumerator / mDenominator;
  if (mValue === 0) return `${d}`;
  const mLatex = simplifyFraction(mNumerator, mDenominator);
  const mPart = mValue === 1 ? "x" : mValue === -1 ? "-x" : `${mLatex}x`;
  if (d === 0) return mPart;
  return d > 0 ? `${mPart} + ${d}` : `${mPart} - ${Math.abs(d)}`;
}

function lineLatexFromUser(aParsed, aRaw, bParsed, bRaw) {
  if (!Number.isFinite(aParsed) || !Number.isFinite(bParsed)) return null;

  const aLatexRaw = String(formatLatexPreview(aRaw || ""));
  const bLatexRaw = String(formatLatexPreview(bRaw || ""));
  const aLatex = aLatexRaw.includes("=") ? aLatexRaw.split("=")[1].trim() : aLatexRaw;
  const bLatex = bLatexRaw.includes("=") ? bLatexRaw.split("=")[1].trim() : bLatexRaw;

  if (aParsed === 0) return `y = ${bLatex}`;

  const aPart = aParsed === 1 ? "x" : aParsed === -1 ? "-x" : `${aLatex}x`;
  if (bParsed === 0) return `y = ${aPart}`;
  return `y = ${aPart} ${bParsed >= 0 ? "+" : "-"} ${bLatex.replace("-", "")}`;
}

function quadraticExpr(a, b, c) {
  const parts = [`${a}x^2`];
  if (b !== 0) parts.push(b > 0 ? `+${b}x` : `${b}x`);
  if (c !== 0) parts.push(c > 0 ? `+${c}` : `${c}`);
  return `y=${parts.join("")}`;
}

function lineExpr(m, d) {
  if (m === 0) return `y=${d}`;
  const parts = [`${m}x`];
  if (d !== 0) parts.push(d > 0 ? `+${d}` : `${d}`);
  return `y=${parts.join("")}`;
}

function helperLineConfig(targetSlope) {
  const relation = choice(["parallel", "perpendicular"]);
  const intercept = randInt(-5, 5, [0]);

  if (relation === "parallel") {
    const factor = choice([1, 2, 3]);
    const shownNumerator = targetSlope * factor;
    const shownDenominator = factor;
    return {
      relation,
      helperSlopeNum: shownNumerator,
      helperSlopeDen: shownDenominator,
      helperSlopeValue: targetSlope,
      helperIntercept: intercept,
      targetSlope,
      helperLineLatex: lineLatexExact(shownNumerator, shownDenominator, intercept),
      relationLatex: "rownolegla",
      slopeReasoningLatex: `a = ${slopeLatex(shownNumerator, shownDenominator)}`,
    };
  }

  return {
    relation,
    helperSlopeNum: -1,
    helperSlopeDen: targetSlope,
    helperSlopeValue: -1 / targetSlope,
    helperIntercept: intercept,
    targetSlope,
    helperLineLatex: lineLatexExact(-1, targetSlope, intercept),
    relationLatex: "prostopadla",
    slopeReasoningLatex: `a = -\\frac{1}{${simplifyFraction(-1, targetSlope).replace("-", "")}} = ${targetSlope}`,
  };
}

function generateCoefficients() {
  for (let attempt = 0; attempt < 800; attempt += 1) {
    const x0 = weightedX0();
    const y0 = randInt(-4, 4);
    const qa = randInt(-2, 2, [0]);
    const targetSlope = randInt(-5, 5, [0]);
    const qb = targetSlope - 2 * qa * x0;
    const qc = y0 - qa * x0 * x0 - qb * x0;
    const d = y0 - targetSlope * x0;
    const helper = helperLineConfig(targetSlope);
    const { xRange, yRange } = buildRanges(x0, y0);

    const coefficientsReasonable = Math.abs(qb) <= 14 && Math.abs(qc) <= 24;
    const tangentReasonable = d >= yRange[0] - 2 && d <= yRange[1] + 2;
    const localVisible = [x0 - 1, x0, x0 + 1]
      .filter((x) => x >= xRange[0] && x <= xRange[1])
      .every((x) => {
        const y = quadraticValue(qa, qb, qc, x);
        return y >= yRange[0] && y <= yRange[1];
      });

    if (coefficientsReasonable && tangentReasonable && localVisible) {
      return {
        qa,
        qb,
        qc,
        x0,
        y0,
        targetSlope,
        d,
        xRange,
        yRange,
        helper,
      };
    }
  }

  const x0 = 3;
  const y0 = 2;
  const qa = 1;
  const targetSlope = 4;
  const qb = targetSlope - 2 * qa * x0;
  const qc = y0 - qa * x0 * x0 - qb * x0;
  const d = y0 - targetSlope * x0;
  const helper = helperLineConfig(targetSlope);
  const { xRange, yRange } = buildRanges(x0, y0);
  return { qa, qb, qc, x0, y0, targetSlope, d, xRange, yRange, helper };
}

export function generateTask() {
  const { qa, qb, qc, x0, y0, targetSlope, d, xRange, yRange, helper } = generateCoefficients();

  return {
    type: "QuadraticTangentBySlopeTask",
    prompt: `Wyznacz rownanie stycznej do wykresu funkcji f(x) = ${quadraticLatex(qa, qb, qc)}, ktora jest ${helper.relation === "parallel" ? "rownolegla" : "prostopadla"} do prostej y = ${helper.helperLineLatex}. Podaj wspolczynniki a i b prostej y = ax + b.`,
    answer: `a=${targetSlope}, b=${d}`,
    data: {
      qa,
      qb,
      qc,
      x0,
      y0,
      tangentA: targetSlope,
      tangentB: d,
      functionLatex: quadraticLatex(qa, qb, qc),
      tangentLatex: lineLatex(targetSlope, d),
      helperLineLatex: helper.helperLineLatex,
      relation: helper.relation,
      relationLatex: helper.relationLatex,
      slopeReasoningLatex: helper.slopeReasoningLatex,
      xRange,
      yRange,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>Wyznacz rownanie stycznej do wykresu funkcji:</p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <p>
        ktora jest{" "}
        <span style={{ textDecoration: "underline" }}>
          {data.relation === "parallel" ? "rownolegla" : "prostopadla"}
        </span>{" "}
        do prostej <InlineMath math={`y = ${data.helperLineLatex}`} />.
      </p>
      <p>Podaj wspolczynniki <InlineMath math={"a"} /> i <InlineMath math={"b"} /> prostej <InlineMath math={"y=ax+b"} />.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer && typeof userAnswer === "object" ? userAnswer : {};
  return (
    <div className="space-y-3">
      <div>
        <div style={{ marginBottom: "0.35rem" }}><InlineMath math="a =" /></div>
        <FractionInput
          value={value.answerA || ""}
          onChange={(next) => onChange({ ...value, answerA: next })}
          disabled={disabled}
          label="a"
        />
      </div>
      <div>
        <div style={{ marginBottom: "0.35rem" }}><InlineMath math="b =" /></div>
        <FractionInput
          value={value.answerB || ""}
          onChange={(next) => onChange({ ...value, answerB: next })}
          disabled={disabled}
          label="b"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const aValue = userAnswer.answerA?.parsed;
  const bValue = userAnswer.answerB?.parsed;
  return typeof aValue === "number" && typeof bValue === "number"
    && Math.abs(aValue - data.tangentA) < 0.0001
    && Math.abs(bValue - data.tangentB) < 0.0001;
}

export function renderUserAnswer(_taskOrAnswer, maybeUserAnswer) {
  const userAnswer = maybeUserAnswer !== undefined ? maybeUserAnswer : _taskOrAnswer;
  if (!userAnswer || typeof userAnswer !== "object") return <i>(brak odpowiedzi)</i>;
  const rawA = userAnswer.answerA?.raw;
  const rawB = userAnswer.answerB?.raw;
  if (!rawA && !rawB) return <i>(brak odpowiedzi)</i>;

  const aValue = userAnswer.answerA?.parsed;
  const bValue = userAnswer.answerB?.parsed;
  const userLineLatex = lineLatexFromUser(aValue, rawA, bValue, rawB);

  return (
    <div className="space-y-2">
      {rawA ? <div><InlineMath math={formatLatexPreview(rawA, "a")} /></div> : <div><InlineMath math={"a = ?"} /></div>}
      {rawB ? <div><InlineMath math={formatLatexPreview(rawB, "b")} /></div> : <div><InlineMath math={"b = ?"} /></div>}
      {userLineLatex ? (
        <div style={{ marginTop: "0.25rem" }}>
          <InlineMath math={userLineLatex} />
        </div>
      ) : null}
    </div>
  );
}

export function renderAnswer({ data }) {
  return (
    <div className="space-y-1">
      <div><InlineMath math={`a = ${data.tangentA}`} /></div>
      <div><InlineMath math={`b = ${data.tangentB}`} /></div>
      <div><InlineMath math={`y = ${data.tangentLatex}`} /></div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const derivativeLatex = `${2 * data.qa}x${data.qb === 0 ? "" : data.qb > 0 ? ` + ${data.qb}` : ` - ${Math.abs(data.qb)}`}`;
  const slopeEquation = `${derivativeLatex} = ${data.tangentA}`;
  const parabolaExpr = quadraticExpr(data.qa, data.qb, data.qc);
  const tangentExpr = lineExpr(data.tangentA, data.tangentB);
  const relationRule = data.relation === "parallel"
    ? "a_1 = a_2"
    : "a_1 \\cdot a_2 = -1";

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p><strong>Krok 1:</strong> Odczytujemy wspolczynnik kierunkowy danej prostej i korzystamy z warunku na proste rownolegle lub prostopadle.</p>
      <BlockMath math={`y = ${data.helperLineLatex}`} />
      <BlockMath math={relationRule} />
      <BlockMath math={data.slopeReasoningLatex} />
      <p><strong>Krok 2:</strong> Liczymy pochodna funkcji kwadratowej.</p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <BlockMath math={`f'(x) = ${derivativeLatex}`} />
      <p><strong>Krok 3:</strong> Szukamy punktu, dla ktorego <InlineMath math={`f'(x) = a`} />.</p>
      <BlockMath math={slopeEquation} />
      <BlockMath math={`x_0 = ${data.x0}`} />
      <p><strong>Krok 4:</strong> Obliczamy wspolrzedna punktu stycznosci.</p>
      <BlockMath math={`f(${data.x0}) = ${data.y0}`} />
      <BlockMath math={`P = (${data.x0}, ${data.y0})`} />
      <p><strong>Krok 5:</strong> Podstawiamy punkt <InlineMath math={`P(${data.x0}, ${data.y0})`} /> oraz wspolczynnik kierunkowy <InlineMath math={`a = ${data.tangentA}`} /> do rownania <InlineMath math={"y = ax + b"} />.</p>
      <BlockMath math={`${data.y0} = ${data.tangentA}\\cdot ${data.x0} + b`} />
      <BlockMath math={`b = ${data.tangentB}`} />
      <p><strong>Krok 6:</strong> Zapisujemy rownanie stycznej.</p>
      <BlockMath math={`y = ${data.tangentLatex}`} />
      <div style={{ maxWidth: 350, margin: "24px auto 0" }}>
        <GraphViewer
          width={350}
          xRange={data.xRange}
          yRange={data.yRange}
          equations={[
            { expr: parabolaExpr, color: "#ff8c1a" },
            { expr: tangentExpr, color: "#9d4edd" },
          ]}
          points={{
            P: { x: data.x0, y: data.y0, label: "P", color: "#cc5500", textColor: "#cc5500", textPosition: "top center" },
          }}
        />
      </div>
    </div>
  );
}
